import toast from "components/partials/toast/toast";

import { getClientConfiguration } from "generated/api/client-configuration";
import { customInstance } from "config/mutator";
import type { JsonNode, ThemeAssetDTO } from "generated/schemas";

import type { APIThemeData } from "containers/admin/clients/client/theme-page/preview/preview.types";
import type { TypekitResponse } from "types";
import type { GoogleFont } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/google-fonts.types";

const clientConfigurationApi = getClientConfiguration();

export interface GoogleFontsResponse {
  items: GoogleFont[];
}

/**
 * Fetches the saved theme configuration for a client. Backed by the generic
 * configuration GET (`get2`), which the backend serves as untyped `JsonNode`.
 */
export async function fetchTheme(clientId: string): Promise<APIThemeData> {
  const data = await clientConfigurationApi.get2({ clientId, config: "theme" });
  return data as unknown as APIThemeData;
}

/**
 * Creates or updates the theme configuration for a client. Shows a success toast.
 */
export async function saveTheme(clientId: string, attributes: APIThemeData): Promise<APIThemeData> {
  const data = await clientConfigurationApi.createOrUpdate(
    { clientId },
    attributes as unknown as JsonNode,
  );
  toast.success({ data: { title: "Saved theme" } });
  return data as unknown as APIThemeData;
}

/**
 * Fetches Adobe Typekit fonts for a client's project.
 */
export async function fetchTypekitFonts(
  clientId: string,
  projectId: string,
): Promise<TypekitResponse> {
  const data = await clientConfigurationApi.getTypekitJson({ clientId, typekitId: projectId });
  return data as unknown as TypekitResponse;
}

/**
 * Uploads a theme image asset (e.g. banner) for a client. Posts
 * `multipart/form-data` via `customInstance` because the generated `assetUpload`
 * serializes the body as JSON, which the backend does not accept for file uploads.
 */
export async function uploadThemeAsset(clientId: string, file: File): Promise<ThemeAssetDTO> {
  const formData = new FormData();
  formData.append("uploadFile", file);

  try {
    return await customInstance<ThemeAssetDTO>({
      url: `/api/clients/${clientId}/theme-assets`,
      method: "POST",
      params: { type: "image" },
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
    });
  } catch (error) {
    console.error("Theme asset upload error:", { error });
    throw error;
  }
}

/**
 * Fetches the list of Google fonts ordered by popularity. Uses `customInstance`
 * directly because the generated `getGoogleFonts` does not expose the `sort` param.
 */
export async function fetchGoogleFonts(): Promise<GoogleFontsResponse> {
  return customInstance<GoogleFontsResponse>({
    url: "/api/fonts",
    method: "GET",
    params: { sort: "popularity" },
  });
}
